import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, Mail, KeyRound, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ActivarInquilinoPage() {
  const navigate = useNavigate();
  // 'email' | 'password' | 'already_active' | 'desactivado' | 'done'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState(null);
  const [nombre, setNombre] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Ingresá tu email');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/inquilinos/activacion/check-email`, {
        email: email.trim().toLowerCase(),
      });
      const estado = res.data?.estado;
      if (estado === 'activo') {
        setStep('already_active');
        return;
      }
      if (estado === 'desactivado') {
        setStep('desactivado');
        return;
      }
      if (res.data?.ok && estado === 'pendiente_activacion') {
        setUserId(res.data.user_id);
        setNombre(res.data.nombre || '');
        setStep('password');
        return;
      }
      setError(res.data?.message || 'No pudimos validar el acceso. Revisá que estés usando el mismo email registrado.');
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Error de conexión.';
      setError(typeof msg === 'string' ? msg : 'Error al validar el email.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/inquilinos/activacion/completar`, {
        user_id: userId,
        email: email.trim().toLowerCase(),
        password,
      });
      toast.success('¡Tu cuenta fue activada!');
      setStep('done');
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Error al activar la cuenta.';
      setError(typeof msg === 'string' ? msg : 'Error al activar la cuenta.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8" data-testid="activar-inq-header">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Activar mi cuenta
          </h1>
          <p className="text-muted-foreground mt-2 text-center text-sm">
            Portal de inquilinos — Global Citizen.
          </p>
        </div>

        <div className="bg-white border border-border rounded-2xl shadow-xl p-8">
          {step === 'email' && (
            <form onSubmit={handleCheckEmail} className="space-y-5" data-testid="activar-inq-form-email">
              <div>
                <Label htmlFor="email" className="text-sm font-medium">Ingresá tu email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tuemail@dominio.com"
                    className="pl-9"
                    autoFocus
                    data-testid="activar-inq-input-email"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Usá el mismo email registrado en tu contrato.
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="activar-inq-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full" data-testid="activar-inq-btn-validar">
                {loading ? 'Validando...' : 'Continuar'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          )}

          {step === 'password' && (
            <form onSubmit={handleSubmitPassword} className="space-y-5" data-testid="activar-inq-form-password">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-800 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Hola{nombre ? ` ${nombre}` : ''}, tu email <strong>{email}</strong> está autorizado.
                  Creá tu contraseña para acceder al portal.
                </span>
              </div>

              <div>
                <Label htmlFor="pwd" className="text-sm">Contraseña <span className="text-red-500">*</span></Label>
                <div className="relative mt-1">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="pwd"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9"
                    placeholder="Mínimo 8 caracteres"
                    data-testid="activar-inq-input-password"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="pwd2" className="text-sm">Repetir contraseña <span className="text-red-500">*</span></Label>
                <Input
                  id="pwd2"
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  className="mt-1"
                  data-testid="activar-inq-input-password-confirm"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="activar-inq-error-pwd">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setStep('email'); setError(''); setPassword(''); setPasswordConfirm(''); }}
                  data-testid="activar-inq-btn-volver"
                >
                  Volver
                </Button>
                <Button type="submit" disabled={loading} className="flex-1" data-testid="activar-inq-btn-completar">
                  {loading ? 'Activando...' : 'Activar mi cuenta'}
                </Button>
              </div>
            </form>
          )}

          {step === 'already_active' && (
            <div className="text-center py-8" data-testid="activar-inq-already-active">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
                Esta cuenta ya fue activada
              </h2>
              <p className="text-muted-foreground mb-6">Ingresá desde la pantalla de login.</p>
              <Button onClick={() => navigate('/login')} className="w-full max-w-xs" data-testid="activar-inq-btn-ir-login">
                Ir a iniciar sesión
              </Button>
            </div>
          )}

          {step === 'desactivado' && (
            <div className="text-center py-8" data-testid="activar-inq-desactivado">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">Acceso no disponible</h2>
              <p className="text-muted-foreground mb-6 text-sm">
                Tu acceso fue desactivado. Contactá a la administración.
              </p>
              <Button variant="outline" onClick={() => { setStep('email'); setError(''); }} className="w-full max-w-xs">
                Volver
              </Button>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-8" data-testid="activar-inq-done">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>¡Listo!</h2>
              <p className="text-muted-foreground mb-6">Tu cuenta fue activada. Ya podés iniciar sesión.</p>
              <Button onClick={() => navigate('/login')} className="w-full max-w-xs" data-testid="activar-inq-btn-login">
                Ir a Iniciar Sesión
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          ¿Necesitás ayuda? Contactá a la administración.
        </p>
      </div>
    </div>
  );
}
